import { Box, type Breakpoint, type SxProps, type Theme, Typography } from "@mui/material";
import { type CSSProperties, type FC, type ReactNode, useId } from "react";
import { FONT_SIZE, INK, LINE_HEIGHT } from "../../../theme/tokens";

type Props = {
	label: string;
	labelFontSize?: Partial<Record<Breakpoint, CSSProperties["fontSize"]>> | CSSProperties["fontSize"];
	description?: ReactNode;
	/** The control for this setting (e.g. a `SystemSwitch`), anchored on the right. */
	control: ReactNode;
	/**
	 * When true, the control fills the available width and is allowed to shrink (`minWidth: 0`) instead of
	 * keeping its intrinsic size. Use for `fullWidth` inputs (text fields, multi-selects) so a wide value —
	 * e.g. a many-chip multi-select — truncates inside the cell rather than overflowing onto neighbors.
	 * Leave off for compact controls (switches) that should keep their natural size on the right.
	 */
	fullWidthControl?: boolean;
	/**
	 * Put the control on its own line **below** the label instead of anchoring it to the right. Use when
	 * the control is a cluster rather than a single input — e.g. a date range plus an action button. Such a
	 * cluster cannot shrink below its intrinsic width, so beside the text it overflows the section's
	 * `contentMaxWidth` instead of fitting; stacking gives it the full row.
	 */
	stacked?: boolean;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/** Labeled settings row (label + optional description left, control right — or below, when `stacked`) for use inside a `SettingsSection`. */
export const SettingRow: FC<Props> = ({ label, labelFontSize = FONT_SIZE.EMPHASIS, description, control, fullWidthControl, stacked, dataTestId, sx }) => {
	const labelId = useId();
	return (
		<Box
			data-testid={dataTestId}
			sx={[
				{
					display: "flex",
					flexDirection: stacked ? "column" : { xs: "column", sm: "row" },
					alignItems: stacked ? "flex-start" : { xs: "flex-start", sm: "center" },
					justifyContent: "space-between",
					gap: { xs: 1, sm: stacked ? 1.5 : 3 },
					width: "100%",
					minWidth: 0,
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
				<Typography id={labelId} component="span" sx={{ fontWeight: 600, fontSize: labelFontSize, lineHeight: 1.3, color: INK.SECONDARY }}>
					{label}
				</Typography>
				{description && (
					<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, lineHeight: LINE_HEIGHT.DENSE_PROSE, color: INK.MUTED }}>
						{description}
					</Typography>
				)}
			</Box>
			{/* `control` is arbitrary — a segmented switch's options carry their own text but nothing tells AT
			    what the group of them is FOR, and the label is a sibling `<span>`, not a real `<label>`. The
			    group + `aria-labelledby` is what connects the two, and is why `labelId` exists at all. */}
			<Box
				role="group"
				aria-labelledby={labelId}
				sx={
					stacked
						? { width: "100%", minWidth: 0 }
						: fullWidthControl
							? { flex: 1, minWidth: 0, width: { xs: "100%", sm: "auto" } }
							: { flexShrink: 0, minWidth: 0 }
				}
			>
				{control}
			</Box>
		</Box>
	);
};
